import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import Select, { SelectItem } from '../ui/Select';
import ApplicationContext, { IApplicationContext } from '../ApplicationContext';
import { Currency } from '../ifmsTypes';
import { forEach } from 'lodash';

function getCurrenciesFromAppContext(appContext:IApplicationContext) {
  if (!!appContext && !!appContext.systemSettings && !!appContext.systemSettings.currencies && appContext.systemSettings.currencies.length > 0) {
    return appContext.systemSettings.currencies;
  }

  return [];
}

export interface CurrencyAdditionalElement {
  value: string,
  label: string,
}

function CurrencyDropdown({
  value,
  onChange = () => {},
  disabled = false,
  items = [],
  selectWidth = undefined,
  selectId = undefined,
  onTabNext = undefined,
  allowEmpty = true,
  additionalElements = [],
  hideCaret = false,
  inactive = false,
  name = undefined,
  notFocusNextEleWhenSelectItem = false,
}: {
  value: string|null,
  onChange?: (x:string|null) => void,
  disabled?: boolean,
  items?: Array<Currency>,
  selectId?: string|undefined,
  selectWidth?: string|undefined,
  onTabNext?: (() => boolean)|undefined,
  allowEmpty?: boolean,
  additionalElements? : Array<CurrencyAdditionalElement>,
  hideCaret?: boolean,
  inactive?: boolean,
  name?:string|undefined
  notFocusNextEleWhenSelectItem?: boolean,
}) {
  const appContext = useContext(ApplicationContext);

  const [data, setData] = useState<Array<Currency>>(items || []);

  async function fetchData(appContext:IApplicationContext) {
    const data = getCurrenciesFromAppContext(appContext);
    setData(data);
  }

  function handleChange(e:React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.value) {
      onChange(null);
      return;
    }
    
    onChange(e.target.value);
  }

  useEffect(() => {
    if (items && items.length > 0) {
      setData(items);
    } else {
      if(!data || data.length === 0) fetchData(appContext);
    }
  },
    //<pt220401: Call fetchData once>
    //[items]
    //[]

    //<jhchoi date="2023-07-20" desc="Invoice or Bill Header Currency 항목이 상황에 따라 변경이 필요해서 items가 변경 된 경우 갱신되도록 수정"
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  // 기본적으로 Inactive 항목은 포함되지 않고 inactive 속성을 true로 설정 할 경우 포함
  let currencies = data;
  if (!inactive) {
    currencies = data.filter(x=> !x.inactive);
  }

  let addItem:Array<SelectItem> = [];
  
  additionalElements.forEach(additionalElement => {
    if(currencies.findIndex(x => x.currency == additionalElement.label && x.currency === additionalElement.value) === -1) {
      addItem.push(additionalElement);
    }
  });

  let displayData:Array<SelectItem> = [
    // ...additionalElements,
    ...addItem,
    ...currencies.map(x => ({ value: x.currency ?? '', label: x.currency ?? '' }))
  ];

  if (allowEmpty) {
    displayData.unshift({value: '', label: '', activeLabel: '' });
  }

  return (
    <Select
      name={name}
      selectId={selectId}
      value={(value === null || value === undefined) ? '' : value}
      onChange={handleChange}
      disabled={disabled}
      items={displayData}
      selectWidth={selectWidth}
      onTabNext={onTabNext}
      hideCaret={hideCaret}
      notFocusNextEleWhenSelectItem={notFocusNextEleWhenSelectItem}
    />
  );
}

CurrencyDropdown.defaultProps = {
  onChange: () => {},
  disabled: false,
  dark: false,
  allowEmpty: true,
  additionalElements: [],
  hideCaret: false,
  inactive: false
};

CurrencyDropdown.propTypes = {
  selectId: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  dark: PropTypes.bool.isRequired,
  items: PropTypes.array,
  onTabNext: PropTypes.func,
  allowEmpty: PropTypes.bool.isRequired,
  additionalElements: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  hideCaret: PropTypes.bool.isRequired,
  inactive: PropTypes.bool
};

export default React.memo(CurrencyDropdown);
